import { inject, Injectable } from "@angular/core";

import { LogService } from "@bitwarden/common/abstractions/log.service";

import { DuplicatesService } from "../gloss/manage/manage-source-explorer/duplicates.service";
import { pickBestTransactionDuplicateKeeperStyleId } from "../gloss/manage/manage-source-explorer/transaction-dedup-keeper";
import { VaultExplorerService } from "@bitwarden/web-vault/app/services/dali/vault-explorer.service";
import {
  StoreModelNameByVaultCode,
  VaultCodeEnum,
} from "@bitwarden/web-vault/app/services/dali/type/dali.type";
import { PerformanceService } from "@bitwarden/web-vault/app/services/performance/performance.service";
import { StoreData } from "@bitwarden/web-vault/app/models/store/store.data";
import { TransactionStoreModel } from "@bitwarden/web-vault/app/models/store/transaction.store.model";
import { StateService } from "@bitwarden/web-vault/app/core";

/**
 * Silent vault maintenance after login (no dialogs / toasts).
 */
@Injectable({
  providedIn: "root",
})
export class CleanupService {
  private vaultExplorer = inject(VaultExplorerService);
  private duplicates = inject(DuplicatesService);
  private performance = inject(PerformanceService);
  private log = inject(LogService);
  private stateService = inject(StateService);

  /**
   * Loads vault cache, finds duplicate **transaction** groups (Style-A then Style-ID), deletes
   * duplicates until `numberOfDupGroups` groups have been processed in total.
   *
   * Style-A: newest-by-`dc` kept per group (same as explorer {@link VaultExplorerService.deleteDuplicates}).
   * Style-ID: same keeper rules as manual explorer cleanup — valid source row, prefer non-Uncategorized
   * category, then newest-by-`dc`.
   *
   * After duplicate transactions are removed, deletes source-transaction vault rows whose ids match
   * deleted txs’ srIds only when no surviving transaction still references that srId (same rules as
   * transaction explorer cleanup).
   */
  async cleanUpDups(numberOfDupGroups: number): Promise<void> {
    if (!numberOfDupGroups || numberOfDupGroups < 1) {
      return;
    }

    try {
      this.performance.start("Load vault types for dedup : Clean up dups");
      await this.vaultExplorer.loadVaultTypes();
      const typeCounts = this.vaultExplorer.vaultTypeCounts();
      if (typeCounts) {
        const totalVaultItems = [...typeCounts.values()].reduce((sum, n) => sum + n, 0);
        this.performance.addLabelsToCurrentTransaction({
          vault_total_items: totalVaultItems,
          vault_tx_count: typeCounts.get(VaultCodeEnum.Transaction) ?? 0,
          vault_src_tx_count: typeCounts.get(VaultCodeEnum.SourceTransaction) ?? 0,
        });
      }
      this.performance.end();

      let quota = numberOfDupGroups;

      this.performance.start("Grouping Style A : Clean up dups");
      const styleAGroups = this.duplicates
        .getTransactionDuplicateGroupsFromVault("A")
        .filter((g) => g.count > 1);
      this.performance.end();

      this.performance.start("Grouping Style ID : Clean up dups");
      const styleIdGroups = this.duplicates
        .getTransactionDuplicateGroupsFromVault("ID")
        .filter((g) => g.count > 1);
      this.performance.end();

      this.performance.start("Getting source id set : Clean up dups");
      const sourceIdSet = new Set(
        this.vaultExplorer
          .getCachedItemsByType(VaultCodeEnum.SourceTransaction)
          .map((s) => String(s.id ?? "").trim())
          .filter(Boolean),
      );
      this.performance.end();

      this.performance.start("Prepping dups for deletion : Clean up dups");
      const txModelName = StoreModelNameByVaultCode[VaultCodeEnum.Transaction];
      const srcModelName = StoreModelNameByVaultCode[VaultCodeEnum.SourceTransaction];

      const allTxToDelete: StoreData[] = [];
      const keptSrIds = new Set<string>();
      const candidateOrphanSrIds = new Set<string>();

      const accumulateCleanupGroup = (kept: StoreData, deleted: StoreData[]): void => {
        const keptSrId = String((kept as TransactionStoreModel).srId ?? "").trim();
        if (keptSrId) {
          keptSrIds.add(keptSrId);
        }
        allTxToDelete.push(...deleted);
        for (const del of deleted) {
          const delSrId = String((del as TransactionStoreModel).srId ?? "").trim();
          if (delSrId) {
            candidateOrphanSrIds.add(delSrId);
          }
        }
      };

      for (const group of styleAGroups) {
        if (quota <= 0) {
          break;
        }
        if (group.count <= 1) {
          continue;
        }
        const sorted = [...group.all].sort(
          (a, b) => new Date(b.dc).getTime() - new Date(a.dc).getTime(),
        );
        const kept = sorted[0];
        const deleted = sorted.slice(1);
        accumulateCleanupGroup(kept, deleted);
        quota--;
      }

      for (const group of styleIdGroups) {
        if (quota <= 0) {
          break;
        }
        if (group.count <= 1) {
          continue;
        }
        const sorted = [...group.all].sort(
          (a, b) => new Date(b.dc).getTime() - new Date(a.dc).getTime(),
        );
        const kept = pickBestTransactionDuplicateKeeperStyleId(sorted, sourceIdSet);
        const deleted = sorted.filter((item) => item !== kept);
        accumulateCleanupGroup(kept, deleted);
        quota--;
      }

      if (allTxToDelete.length === 0) {
        this.performance.end();
        this.performance.start("Cleanup complete : Clean up dups");
        this.performance.addLabelsToCurrentTransaction({
          cleanup_complete: true,
          deleted_tx_count: 0,
          deleted_src_tx_count: 0,
        });
        this.performance.end();
        return;
      }

      const deleteById = new Map<string, StoreData>();
      for (const tx of allTxToDelete) {
        const id = String(tx.id ?? "").trim();
        if (id) {
          deleteById.set(id, tx);
        }
      }
      const uniqueTxToDelete = [...deleteById.values()];

      for (const id of keptSrIds) {
        candidateOrphanSrIds.delete(id);
      }

      const deletedTxIds = new Set(uniqueTxToDelete.map((d) => String(d.id ?? "").trim()));
      const allTransactions = this.vaultExplorer.getCachedItemsByType(VaultCodeEnum.Transaction);

      for (const srId of [...candidateOrphanSrIds]) {
        const stillReferenced = allTransactions.some((tx) => {
          const txId = String(tx.id ?? "").trim();
          const txSrId = String((tx as TransactionStoreModel).srId ?? "").trim();
          return txSrId === srId && !deletedTxIds.has(txId);
        });
        if (stillReferenced) {
          candidateOrphanSrIds.delete(srId);
        }
      }

      const allSourceTxs = this.vaultExplorer.getCachedItemsByType(VaultCodeEnum.SourceTransaction);
      const deletedSourceTxs: StoreData[] = [];
      for (const srcTx of allSourceTxs) {
        const srcId = String(srcTx.id ?? "").trim();
        if (!srcId || !candidateOrphanSrIds.has(srcId)) {
          continue;
        }
        deletedSourceTxs.push(srcTx);
      }
      this.performance.end();

      this.performance.start("Deleting transaction rows : Clean up dups");
      const duplicateRowCountStyleA = styleAGroups.reduce((sum, g) => sum + g.count, 0);
      const duplicateRowCountStyleId = styleIdGroups.reduce((sum, g) => sum + g.count, 0);
      const userId = String((await this.stateService.getUserId()) ?? "").trim();
      this.performance.addLabelsToCurrentTransaction({
        user_id: userId || "unknown",
        total_transactions: allTransactions.length,
        total_duplicate_transactions: Math.max(duplicateRowCountStyleA, duplicateRowCountStyleId),
        deleted_duplicate_transactions: numberOfDupGroups,
      });
      await this.vaultExplorer.deleteVaultItems(txModelName, uniqueTxToDelete);
      this.performance.end();

      this.performance.start("Deleting source transcations of the deleted ones  : Clean up dups");
      try {
        if (deletedSourceTxs.length > 0) {
          await this.vaultExplorer.deleteVaultItems(srcModelName, deletedSourceTxs);
        }
      } finally {
        this.performance.end();
      }

      await this.vaultExplorer.reloadVaultDataForType(VaultCodeEnum.Transaction);
      if (deletedSourceTxs.length > 0) {
        await this.vaultExplorer.reloadVaultDataForType(VaultCodeEnum.SourceTransaction);
      }

      this.performance.start("Cleanup complete : Clean up dups");
      this.performance.addLabelsToCurrentTransaction({
        cleanup_complete: true,
        deleted_tx_count: uniqueTxToDelete.length,
        deleted_src_tx_count: deletedSourceTxs.length,
      });
      this.performance.end();
    } catch (e) {
      this.log.error("CleanupService.cleanUpDups failed");
      this.log.error(e);
    } finally {
      this.performance.end();
    }
  }
}
